import { type Collection, List as ImmutableList, Map as ImmutableMap } from 'immutable';
import { DateTime } from 'luxon';
import numeral from 'numeral';

import { type accountsTypes, accountsUtils } from 'app-components/flux/accounts';
import {
  chartOfAccountsSelectors,
  chartOfAccountsTypes,
  chartOfAccountsUtils,
} from 'app-components/flux/chart-of-accounts';
import { getId } from 'app-components/flux/core/resourceStoreTypes';
import { customersSelectors } from 'app-components/flux/customers';
import type { FEATURE_FLAGS_REDUCER_KEY } from 'app-components/flux/feature-flags';
import type { filtersTypes } from 'app-components/flux/filters';
import { projectsSelectors } from 'app-components/flux/projects';
import { securitiesSelectors } from 'app-components/flux/securities';
import type { SecurityProps } from 'app-components/flux/securities/securitiesTypes';
import { tagsSelectors, tagsTypes } from 'app-components/flux/tags';
import { numberUtils } from 'app-components/utils/number';
import store from 'app-components/utils/redux-store';
import type RootState from 'app-components/utils/redux-store/rootState';

import { ACCOUNTS_REDUCER_KEY } from '../accounts/accountsTypes';
import {
  type Allocation,
  type AllocationProps,
  InvestmentTxnType,
  type SplitItemProps,
  type SplitProps,
  type TransactionItem,
  type TransactionProps,
  type TransactionRecord,
  calTxnLabelTypes,
} from './transactionsTypes';

// -------------------------------------------------------
// isSplitTxn
// made to work with JS objects and immutables
export function isSplitTxn(
  txn: { split?: { items?: Array<any> | ImmutableList<any> | null } | null } | null | undefined,
): boolean {
  return Boolean(
    txn &&
      txn.split &&
      txn.split.items &&
      ('size' in txn.split.items ? txn.split.items.size > 0 : txn.split.items.length > 0),
  );
}

export const isInvoiceTxn = (txn: TransactionRecord) => txn.type === 'INVOICE' && txn.subtype === 'INVOICE';

// -------------------------------------------------------
// isTransferTxn
// only actual transfers - the bast 'transfer' category is technically a balance adjustment, so won't be true for this
// use 'isUITransferTxn' if you need the L1 Transfer category
export function isTransferTxn(txn: Partial<TransactionProps> | null | undefined): boolean {
  if (isSplitTxn(txn)) {
    return Boolean(txn?.split?.items?.find((item) => Boolean(item.coa && item.coa.type === 'ACCOUNT')));
  }
  return Boolean(txn && txn.coa && txn.coa.type === 'ACCOUNT' && txn.coa.id !== txn.accountId);
}

// -------------------------------------------------------
// isUITransferTxn
// includes the l1 'Transfer' option as well as actual account COA's
export function isUITransferTxn(txn: TransactionProps | null | undefined): boolean {
  if (isSplitTxn(txn)) {
    return Boolean(
      txn?.split?.items?.find((item) =>
        Boolean(
          item.coa &&
            (item.coa.type === 'ACCOUNT' ||
              (item.coa.type === 'BALANCE_ADJUSTMENT' &&
                item.coa.id !== chartOfAccountsTypes.BalanceAdjustmentTypeEnum.CREDIT_CARD_PAYMENT)),
        ),
      ),
    );
  }
  return Boolean(
    txn &&
      txn.coa &&
      ((txn.coa.type === 'ACCOUNT' && txn.coa.id !== txn.accountId) ||
        (txn.coa.type === 'BALANCE_ADJUSTMENT' &&
          txn.coa.id !== chartOfAccountsTypes.BalanceAdjustmentTypeEnum.CREDIT_CARD_PAYMENT)),
  );
}

export function isTransferOrCreditCardPaymentTxn(txn: TransactionProps | SplitItemProps | null | undefined): boolean {
  if (isSplitTxn(txn as TransactionProps)) {
    return Boolean(
      (txn as TransactionProps).split?.items?.find((item) =>
        Boolean(
          item.coa &&
            (item.coa.type === 'ACCOUNT' ||
              (item.coa.type === 'BALANCE_ADJUSTMENT' &&
                (item.coa.id === chartOfAccountsTypes.BalanceAdjustmentTypeEnum.CREDIT_CARD_PAYMENT ||
                  item.coa.id === chartOfAccountsTypes.BalanceAdjustmentTypeEnum.TRANSFER))),
        ),
      ),
    );
  }
  return Boolean(
    txn &&
      txn.coa &&
      ((txn.coa.type === 'ACCOUNT' &&
        (!('accountId' in txn) || ('accountId' in txn && txn.coa.id !== txn.accountId))) ||
        (txn.coa.type === 'BALANCE_ADJUSTMENT' &&
          (txn.coa.id === chartOfAccountsTypes.BalanceAdjustmentTypeEnum.CREDIT_CARD_PAYMENT ||
            txn.coa.id === chartOfAccountsTypes.BalanceAdjustmentTypeEnum.TRANSFER))),
  );
}

// -------------------------------------------------------
// isGeneralTransferTxn
// Used for matching recurring series to txns. Is more inclusive in case the series is less-explicitly mapped
export function isGeneralTransferTxn(txn: TransactionProps | null | undefined): boolean {
  if (isSplitTxn(txn)) {
    return Boolean(
      txn?.split?.items?.find((item) =>
        Boolean(
          item.coa &&
            (item.coa.type === 'ACCOUNT' ||
              item.coa.type === chartOfAccountsTypes.CoaTypeEnum.BALANCE_ADJUSTMENT ||
              item.coa.type === chartOfAccountsTypes.CoaTypeEnum.UNCATEGORIZED),
        ),
      ),
    );
  }
  return Boolean(
    txn &&
      txn.coa &&
      ((txn.coa.type === 'ACCOUNT' && txn.coa.id !== txn.accountId) ||
        txn.coa.type === chartOfAccountsTypes.CoaTypeEnum.BALANCE_ADJUSTMENT ||
        txn.coa.type === chartOfAccountsTypes.CoaTypeEnum.UNCATEGORIZED),
  );
}

// TODO -------------------------------------------------------------------
// TODO UNIT TESTS!!!
// The core function that all transfer logic depends on, it uses fuzzy logic
// to find the other side of the of a transfer transaction
//
// findMatchingTransferTx - find the matching transfer for <txn> in the list of transactions <txnList>.
//                          match amount is txn.amount, or 'altAmount' if provided
//
// Parms:
//        txn: QCS transaction record
//        txnList: list of QCS transactions from the account on the "other side" of the transfer.  This
//                 list should be pre-filtered to only contain transfer transactions.
//                 e.g. txnList = accountTransactions.filter((x) => isTransferTxn(x));
//        altAmount: override of txn.amount if desired. This is typically used when isSplitTxn(txn) and
//                   we are searching for one the other side of a split item.  In this case we pass in
//                   the split.item[index].amount rather than using txn.amount
//
// The Algorithm:
//
// For each transaction in txnList, we determine if it is a "candidate" for a possible match.  This
// is determined by 4 facets.  Amount (must be exact negative match), Date (distance between dates)
// Payee (exact string match), and coa (coa must be type ACCOUNT, and id must match txn.accountId)
//
// A candidate will exactly match Coa and Amount.  Note, the 4 facets apply individually to split lines
// if the transaction being examined is split.
//
// Once all candidates are determined, they are sorted first by the date delta (exact date matches sort
// to the top), and using payee match as a secondary sort. We then simply return the top sorted item.
//
// IMPROVEMENTS:
//
// This matching will succeed in 99% of the cases.  Error cases can be made to occur by creating transfers
// on the same day, for the same amount, with the same payee name.  We are accepting of imperfection in
// this case since the "error" is virtually meaningless to the user.  In fact, from an accounting perspective,
// there is no error.  If the user placed different tags, memo, or attachments to these transactions, then
// there may be a situation where a mismatch in this extremely rare case causes an issue.  We are NOT solving
// for this very extreme corner case.
//
// However, it would be easy to add the following improvements:
// a) add memo and tags string values as tertiary sorts
// b) use string-similarity (Dice's Coefficient, available in open source libraries) for payee match
//    for slight differences that may have been created.
// c) use 'number of attachments' as a facet as well
//
// It is hard to envision real life use cases where these corner cases matter (we have MUCH bigger fish
// to fry for our users in more core cases in all our clients), but these are ways we could address this
// if desired.
//
//
export function findMatchingTransferTx(
  txn: Partial<TransactionProps>,
  txnList: ImmutableMap<string, TransactionRecord>,
  altAmount: number | null = null,
) {
  // we look through txnList for a transaction that matches the given transaction.
  // best indicator is date and amount, and of course, the coa
  //

  if (!txnList) {
    return null;
  }

  const srcDate = DateTime.fromISO(txn.postedOn ?? '');
  const txAmount = altAmount || txn.amount;

  // quick lookup if the 'transfer' field has valid ID/clientId
  let maybeTxn;
  if (txn.transfer?.id) {
    maybeTxn = txnList.get(txn.transfer?.id);
  } else if (txn.transfer?.clientId) {
    maybeTxn = txnList.find((tx) => tx.clientId === txn.transfer?.clientId);
  }
  if (maybeTxn && txn.coa?.id === maybeTxn.accountId && maybeTxn.coa?.id === txn.accountId) {
    // verify still connected via COA
    return { txn: maybeTxn, splitItem: null };
  }

  // fall back to sorting and weighting all txns in the bucket
  let txnCandidates: Array<{
    dateFacet: number;
    payeeFacet: boolean;
    txn: TransactionProps;
    splitItem: number | null;
  }> = [];
  txnList.forEach((listTxn) => {
    let amountFacet;
    let coaFacet;
    const dateFacet = srcDate.diff(DateTime.fromISO(listTxn.postedOn), 'days').as('days');
    const payeeFacet = listTxn.payee === txn.payee;

    // amount must match
    // coa's must match
    // payee & date match helps
    if (isSplitTxn(listTxn)) {
      listTxn.split?.items?.forEach((splitItem, index) => {
        if (
          splitItem.coa?.type === 'ACCOUNT' &&
          splitItem.coa.id === txn.accountId &&
          Number(splitItem.amount) === -Number(txAmount)
        ) {
          txnCandidates.push({ dateFacet, payeeFacet, txn: listTxn, splitItem: index });
        }
      });
    } else {
      amountFacet = Number(listTxn.amount) === -Number(txAmount);
      coaFacet = listTxn.coa && listTxn.coa.type === 'ACCOUNT' && listTxn.coa.id === txn.accountId;

      if (amountFacet && coaFacet) {
        txnCandidates.push({ dateFacet, payeeFacet, txn: listTxn, splitItem: null });
      }
    }
  });

  if (txnCandidates.length > 1) {
    // sort the list, with the best match coming to the top
    txnCandidates = txnCandidates.sort((a, b) => {
      // payee match is first priority
      if (a.payeeFacet !== b.payeeFacet) {
        return a.payeeFacet ? -1 : 1;
      }
      // next sort by date delta/difference
      if (a.dateFacet !== b.dateFacet) {
        return Math.abs(a.dateFacet) - Math.abs(b.dateFacet);
      }
      return 0;
    });
  }

  // TODO fix this bug if txCanddidates is empty
  return txnCandidates[0];
}

// used for income/bill type abstraction based on category and isBill/Subscription flags
export function isIncomeFromTxn(
  txn: TransactionProps | null | undefined,
  incomeCOAs: { has: (id: string) => boolean } | null | undefined,
): boolean {
  if (!txn) return false;
  if (txn.isSubscription || txn.isBill) return false;
  if (txn.coa) {
    return incomeCOAs?.has(txn.coa.id) ?? false;
  }
  if (isSplitTxn(txn)) {
    // split
    return Boolean(
      txn.split?.items &&
        txn.split.items.find((item) => Boolean(item.coa && item.coa.id && incomeCOAs?.has(item.coa.id))),
    );
  }
  return false;
}

export const InvestmentTxnActionsByType = Object.freeze({
  [InvestmentTxnType.BUY]: 'Buy',
  [InvestmentTxnType.SELL]: 'Sell',
  [InvestmentTxnType.STOCK_DIVIDEND]: 'Stock Dividend',
  [InvestmentTxnType.REINVEST_DIVIDEND]: 'Reinvest Dividend',
  [InvestmentTxnType.REINVEST_INTEREST]: 'Reinvest Interest',
  [InvestmentTxnType.REINVEST_CAPITAL_GAIN_LONG]: 'Reinvest Long-term Capital Gain',
  [InvestmentTxnType.REINVEST_CAPITAL_GAIN_SHORT]: 'Reinvest Short-term Capital Gain',
  [InvestmentTxnType.INCOME_DIVIDEND]: 'Income Dividend',
  [InvestmentTxnType.INCOME_INTEREST]: 'Income Interest',
  [InvestmentTxnType.CAPITAL_GAIN_LONG]: 'Income Long-term Capital Gain',
  [InvestmentTxnType.CAPITAL_GAIN_SHORT]: 'Income Short-term Capital Gain',
  [InvestmentTxnType.INCOME_MISCELLANEOUS]: 'Income Miscellaneous',
  [InvestmentTxnType.ADD_SHARES]: 'Add Shares',
  [InvestmentTxnType.REMOVE_SHARES]: 'Remove Shares',
  [InvestmentTxnType.BUY_BONDS]: 'Buy Bonds',
  [InvestmentTxnType.SELL_BONDS]: 'Sell Bonds',
  [InvestmentTxnType.EXPENSE_MARGIN_INTEREST]: 'Margin Interest Expense',
  [InvestmentTxnType.EXPENSE_MISCELLANEOUS]: 'Miscellaneous Expense',
  [InvestmentTxnType.RETURN_OF_CAPITAL]: 'Return of Capital',
  [InvestmentTxnType.STOCK_SPLIT]: 'Stock Split',
  [InvestmentTxnType.SHORT_SELL]: 'Short Sell',
  [InvestmentTxnType.COVER_SHORT_SELL]: 'Buy to Cover',
  [InvestmentTxnType.UNKNOWN]: '',
  VIRTUAL_CASH_FLOW: 'Payment/Deposit',
  VIRTUAL_TRANSFER_SHARES: 'Transfer Shares',
});

export const investmentTransactionDescription = (transaction: TransactionProps, securityName?: string | null) => {
  let description = '-';
  // NOTE: txns of manual securities do not have the securityName property
  const security = transaction.symbol || securityName || transaction.securityName;
  const pricePerUnit = Math.abs(
    ((transaction.amount || transaction.costBasis || 0) - (transaction.commission || 0)) / (transaction.units || 1),
  );
  if (
    isValidInvestmentTxnType(transaction.investmentTxnType) &&
    transaction.units &&
    security &&
    typeof pricePerUnit === 'number'
  ) {
    // const units = transaction.units > 0 ? `+${transaction.units}` : Math.abs(transaction.units);
    const units = Math.abs(transaction.units);
    if (pricePerUnit) {
      description = `${units} ${security} shares @ ${pricePerUnit?.toFixed?.(4)}`;
    } else {
      description = `${units} ${security} shares`;
    }
  } else if (transaction.payee) {
    description = transaction.payee;
  }
  return description;
};

export function isBankDownloadedTxn(txn: TransactionProps): boolean {
  return Boolean(txn && txn.cpData);
}

export const isAcceptedScheduledTxn = (txn: TransactionProps): boolean => txn.source === 'SCHEDULED_TRANSACTION';
export const isUnacceptedScheduledTxn = (txn: TransactionProps): boolean =>
  txn.source === 'SCHEDULED_TRANSACTION_PENDING';

export const isCancelledA2aTransfer = (txn: TransactionProps): boolean => txn?.subtype === 'A2A_SHADOW_TXN';
export const isPlannedA2ATransfer = (txn: TransactionProps): boolean =>
  txn?.subtype === 'A2A_TRANSFER' && txn?.matchState === 'NOT_MATCHED';

export const isRefundTxn = (txn: TransactionProps): boolean =>
  txn?.subtype === 'REFUND_SHADOW_TXN' || txn?.subtype === 'REFUND';
export const isRefundIncompleteTxn = (txn: TransactionProps): boolean =>
  isRefundTxn(txn) && getRefundState(txn) !== RefundState.COMPLETE;

export const RefundState = Object.freeze({
  EXPECTED: 'EXPECTED',
  OVERDUE: 'OVERDUE',
  COMPLETE: 'COMPLETE',
  SHADOW: 'SHADOW',
  NONREFUND: 'NONREFUND',
});

// All the nested transaction fields can trigger errors when clients are using plain js versions, just using the specific fields this utility actually needs makes it easier
export const getRefundState = (transaction: Pick<TransactionProps, 'subtype' | 'matchState' | 'postedOn'> | null) => {
  let refundState;

  switch (transaction?.subtype) {
    case 'REFUND':
      switch (transaction.matchState) {
        case 'NOT_MATCHED':
        case 'USER_UNMATCHED':
          {
            const dateNow = DateTime.local().startOf('day');
            const dateRefund = DateTime.fromISO(transaction.postedOn);
            if (dateRefund < dateNow) {
              refundState = RefundState.OVERDUE;
            } else {
              refundState = RefundState.EXPECTED;
            }
          }
          break;
        case 'AUTO_MATCHED':
        case 'USER_MATCHED':
        default:
          refundState = RefundState.COMPLETE;
      }
      break;
    case 'REFUND_SHADOW_TXN':
      refundState = RefundState.SHADOW;
      break;
    default:
      refundState = RefundState.NONREFUND;
  }

  return refundState;
};

// -------------------------------------------------------
// isUpcoming
export function isUpcoming(txn: TransactionProps | null | undefined, today = DateTime.local().toISODate()): boolean {
  return Boolean(
    txn &&
      (txn.source === 'SCHEDULED_TRANSACTION_PENDING' ||
        txn.postedOn > (today ?? '') ||
        ((isRefundTxn(txn) || isPlannedA2ATransfer(txn)) &&
          txn?.matchState !== 'AUTO_MATCHED' &&
          txn?.matchState !== 'USER_MATCHED')),
  );
}

export const isGoalTxnInNonGoalAccount = (
  txn: TransactionProps,
  goalAccountIds: { contains: (id: string) => boolean },
) =>
  txn.coa &&
  txn.coa.type === 'ACCOUNT' &&
  goalAccountIds.contains(txn.coa.id) &&
  !goalAccountIds.contains(txn.accountId || '');

//-----------------------------------------------------------------------
//
// getMatchState
//
// For any transaction will return the following
//
// 'manual' - the transaction has no cpData
// 'matched' - the transaction has cpData and is matchstate is one of the MATCHED states
// 'downloaded' - the transaction has cpData, is not matched, and is not reviewed
//
export function getMatchState(txn: TransactionProps) {
  if (txn.cpData) {
    if (txn.matchState === 'AUTO_MATCHED' || txn.matchState === 'USER_MATCHED') {
      return 'matched';
    }
    return 'downloaded';
  }
  return 'manual';
}

/*
 * getTxnStateInfo
 *
 * This is a mapping from txn SOURCE and STATUS to an object that indicates display information for the transaction
 * object returns contains 'entry', which is either 'bank', 'matched', 'manual', or 'scheduled', and a status which is a combination of
 * transaction state and source, and is one of the following:
 * PENDING
 * CLEARED
 * UPCOMING
 * DUE (Due Today)
 * OVERDUE
 */
export function getTxnStateInfo(txn: TransactionProps) {
  const today = DateTime.local().startOf('day');
  const txnDay = DateTime.fromISO(txn.postedOn).startOf('day');
  const future = txnDay > today;

  const retObj: {
    entry: 'bank' | 'manual' | 'matched' | 'scheduled' | 'unmatched';
    status?: 'PENDING' | 'CLEARED' | 'UPCOMING' | 'DUE' | 'OVERDUE' | 'RECONCILED';
  } = {
    entry: getMatchState(txn) === 'matched' ? 'matched' : 'bank',
    status: txn.state,
  };

  switch (txn.source && txn.source.trim()) {
    case 'QCS_REFRESH':
    case 'QCS_BATCH_REFRESH':
    case 'CLIENT_AGGREGATED':
      break;

    case 'QCS_REFRESH_BANK_PENDING':
    case 'QCS_BATCH_REFRESH_BANK_PENDING':
    case 'CLIENT_AGGREGATED_BANK_PENDING':
    case 'REAL_TIME':
      break;

    case 'USER_ENTERED':
      retObj.entry = getMatchState(txn) === 'matched' ? 'matched' : 'manual';
      if (txn.state === 'CLEARED' || txn.state === 'RECONCILED') {
        retObj.status = txn.state;
      } else {
        retObj.status = future ? 'UPCOMING' : 'PENDING';
      }
      break;

    case 'USER_OWNED_BANK_PENDING':
    case 'USER_OWNED_REAL_TIME_BANK_PENDING':
      break;

    case 'SCHEDULED_TRANSACTION_PENDING':
      retObj.status = future ? 'UPCOMING' : 'OVERDUE';
      if (txnDay.hasSame(today, 'day')) {
        retObj.status = 'DUE';
      }
      retObj.entry = retObj.entry === 'matched' ? 'matched' : 'scheduled';
      break;

    case 'SCHEDULED_TRANSACTION_UNKNOWN':
    case 'SCHEDULED_TRANSACTION': {
      if (txn.state === 'PENDING') {
        retObj.status = future ? 'UPCOMING' : 'PENDING';
      } else {
        retObj.status = 'CLEARED';
      }
      retObj.entry = retObj.entry === 'matched' ? 'matched' : 'scheduled';
      break;
    }

    case 'SCHEDULED_TRANSACTION_SKIPPED':
      // not applicable, this transaction should not be shown (soft deleted)
      break;

    case 'UNKNOWN':
      // should never happen - throw an event?
      retObj.entry = 'manual';
      retObj.status = future ? 'UPCOMING' : 'CLEARED';
      break;

    default:
      // this should REALLY never happen
      break;
  }

  return retObj;
}

/*
 * CalendarTxnType can be [income | expense | overdue | normal | more]
 */
export const getCalendarTxnType = (
  txn: TransactionProps,
  incomeCOAs: { has: (id: string) => boolean } | null | undefined,
) => {
  const txnState = getTxnStateInfo(txn);
  const isIncome = isIncomeFromTxn(txn, incomeCOAs);

  let type: keyof typeof calTxnLabelTypes = calTxnLabelTypes.normal;

  if (txnState.status === 'OVERDUE') {
    type = calTxnLabelTypes.overdue;
  }

  if (txnState.status !== 'OVERDUE' && txnState.entry === 'scheduled') {
    type = isIncome ? calTxnLabelTypes.income : calTxnLabelTypes.expense;

    if (isAcceptedScheduledTxn(txn)) {
      type = calTxnLabelTypes.normal; // Entered txn
    }
  }

  return type;
};

export const isManualTransaction = (transaction: TransactionProps) =>
  transaction.source === 'USER_ENTERED' && transaction.matchState === 'NOT_MATCHED';

export const normalizeInvestmentTransaction = (investmentTransaction: TransactionProps) => {
  const investmentTransactionNormalized = { ...investmentTransaction };

  investmentTransactionNormalized.type = 'INVESTMENT';

  // units
  switch (investmentTransaction.investmentTxnType) {
    case InvestmentTxnType.BUY:
    case InvestmentTxnType.REINVEST_DIVIDEND:
    case InvestmentTxnType.REINVEST_INTEREST:
    case InvestmentTxnType.REINVEST_CAPITAL_GAIN_LONG:
    case InvestmentTxnType.REINVEST_CAPITAL_GAIN_SHORT:
    case InvestmentTxnType.ADD_SHARES:
    case InvestmentTxnType.BUY_BONDS:
    case InvestmentTxnType.COVER_SHORT_SELL:
    case InvestmentTxnType.STOCK_DIVIDEND:
      investmentTransactionNormalized.units = Math.abs(investmentTransaction.units ?? 0);
      break;
    case InvestmentTxnType.SELL:
    case InvestmentTxnType.REMOVE_SHARES:
    case InvestmentTxnType.SELL_BONDS:
    case InvestmentTxnType.SHORT_SELL:
      investmentTransactionNormalized.units = -Math.abs(investmentTransaction.units ?? 0);
      break;
    case InvestmentTxnType.INCOME_DIVIDEND:
    case InvestmentTxnType.INCOME_INTEREST:
    case InvestmentTxnType.CAPITAL_GAIN_LONG:
    case InvestmentTxnType.CAPITAL_GAIN_SHORT:
    case InvestmentTxnType.INCOME_MISCELLANEOUS:
    case InvestmentTxnType.EXPENSE_MARGIN_INTEREST:
    case InvestmentTxnType.EXPENSE_MISCELLANEOUS:
    case InvestmentTxnType.RETURN_OF_CAPITAL:
    case InvestmentTxnType.UNKNOWN:
      investmentTransactionNormalized.units = 0;
      break;
    case InvestmentTxnType.STOCK_SPLIT:
      investmentTransactionNormalized.units =
        Math.abs(investmentTransaction.units ?? 0) *
        Math.sign((investmentTransaction.splitNumerator ?? 0) - (investmentTransaction.splitDenominator ?? 0));
      break;
    default:
  }

  // amount
  switch (investmentTransaction.investmentTxnType) {
    case InvestmentTxnType.BUY:
    case InvestmentTxnType.BUY_BONDS:
    case InvestmentTxnType.COVER_SHORT_SELL:
    case InvestmentTxnType.EXPENSE_MARGIN_INTEREST:
    case InvestmentTxnType.EXPENSE_MISCELLANEOUS:
      investmentTransactionNormalized.amount = -Math.abs(investmentTransaction.amount ?? 0);
      break;
    case InvestmentTxnType.SELL:
    case InvestmentTxnType.INCOME_DIVIDEND:
    case InvestmentTxnType.INCOME_INTEREST:
    case InvestmentTxnType.CAPITAL_GAIN_LONG:
    case InvestmentTxnType.CAPITAL_GAIN_SHORT:
    case InvestmentTxnType.INCOME_MISCELLANEOUS:
    case InvestmentTxnType.RETURN_OF_CAPITAL:
    case InvestmentTxnType.SELL_BONDS:
    case InvestmentTxnType.SHORT_SELL:
      investmentTransactionNormalized.amount = +Math.abs(investmentTransaction.amount ?? 0);
      break;
    case InvestmentTxnType.STOCK_DIVIDEND:
    case InvestmentTxnType.ADD_SHARES:
    case InvestmentTxnType.REMOVE_SHARES:
    case InvestmentTxnType.REINVEST_DIVIDEND:
    case InvestmentTxnType.REINVEST_INTEREST:
    case InvestmentTxnType.REINVEST_CAPITAL_GAIN_LONG:
    case InvestmentTxnType.REINVEST_CAPITAL_GAIN_SHORT:
    case InvestmentTxnType.STOCK_SPLIT:
    case InvestmentTxnType.UNKNOWN:
      investmentTransactionNormalized.amount = 0;
      break;
    default:
  }

  // costBasis
  switch (investmentTransaction.investmentTxnType) {
    case InvestmentTxnType.BUY:
    case InvestmentTxnType.SHORT_SELL:
    case InvestmentTxnType.ADD_SHARES:
    case InvestmentTxnType.REINVEST_DIVIDEND:
    case InvestmentTxnType.REINVEST_INTEREST:
    case InvestmentTxnType.REINVEST_CAPITAL_GAIN_LONG:
    case InvestmentTxnType.REINVEST_CAPITAL_GAIN_SHORT:
      if (investmentTransactionNormalized.amount) {
        investmentTransactionNormalized.costBasis = investmentTransactionNormalized.amount;
      } else {
        investmentTransactionNormalized.costBasis = -Math.abs(investmentTransactionNormalized.costBasis ?? 0);
      }
      break;
    case InvestmentTxnType.COVER_SHORT_SELL:
    case InvestmentTxnType.EXPENSE_MARGIN_INTEREST:
    case InvestmentTxnType.EXPENSE_MISCELLANEOUS:
    case InvestmentTxnType.SELL:
    case InvestmentTxnType.INCOME_DIVIDEND:
    case InvestmentTxnType.INCOME_INTEREST:
    case InvestmentTxnType.CAPITAL_GAIN_LONG:
    case InvestmentTxnType.CAPITAL_GAIN_SHORT:
    case InvestmentTxnType.INCOME_MISCELLANEOUS:
    case InvestmentTxnType.RETURN_OF_CAPITAL:
    case InvestmentTxnType.SELL_BONDS:
    case InvestmentTxnType.STOCK_DIVIDEND:
    case InvestmentTxnType.REMOVE_SHARES:
    case InvestmentTxnType.STOCK_SPLIT:
    case InvestmentTxnType.UNKNOWN:
      investmentTransactionNormalized.costBasis = 0;
      break;
    case InvestmentTxnType.BUY_BONDS: // cost basis different from amount caused by accrued interest
      break;
    default:
  }

  // commission
  switch (investmentTransaction.investmentTxnType) {
    case InvestmentTxnType.ADD_SHARES:
    case InvestmentTxnType.EXPENSE_MARGIN_INTEREST:
    case InvestmentTxnType.EXPENSE_MISCELLANEOUS:
    case InvestmentTxnType.CAPITAL_GAIN_LONG:
    case InvestmentTxnType.CAPITAL_GAIN_SHORT:
    case InvestmentTxnType.INCOME_DIVIDEND:
    case InvestmentTxnType.INCOME_INTEREST:
    case InvestmentTxnType.INCOME_MISCELLANEOUS:
    case InvestmentTxnType.REMOVE_SHARES:
    case InvestmentTxnType.RETURN_OF_CAPITAL:
    case InvestmentTxnType.STOCK_DIVIDEND:
    case InvestmentTxnType.STOCK_SPLIT:
    case InvestmentTxnType.UNKNOWN:
      if (investmentTransactionNormalized.commission) {
        investmentTransactionNormalized.commission = 0;
      }
      break;
    default:
  }

  // splitNumerator + splitDenominator
  switch (investmentTransaction.investmentTxnType) {
    case InvestmentTxnType.STOCK_SPLIT:
      investmentTransactionNormalized.splitNumerator = Math.abs(investmentTransaction.splitNumerator ?? 0);
      investmentTransactionNormalized.splitDenominator = Math.abs(investmentTransaction.splitDenominator ?? 0);
      break;
    default:
      investmentTransactionNormalized.splitNumerator = 0;
      investmentTransactionNormalized.splitDenominator = 0;
  }

  // coa
  switch (investmentTransaction.investmentTxnType) {
    case InvestmentTxnType.UNKNOWN:
    case InvestmentTxnType.EXPENSE_MISCELLANEOUS:
    case InvestmentTxnType.INCOME_MISCELLANEOUS:
      // keep coa
      break;
    default:
      investmentTransactionNormalized.coa = { type: 'UNCATEGORIZED', id: '0' }; // erase
  }

  return investmentTransactionNormalized;
};

export const calcPricePerUnit = (transaction?: TransactionProps | null) => {
  let pricePerUnit;
  if (transaction) {
    const costBasis = transaction?.costBasis || transaction?.amount;
    if (transaction?.units && costBasis) {
      switch (transaction.investmentTxnType) {
        case InvestmentTxnType.BUY:
        case InvestmentTxnType.BUY_BONDS:
        case InvestmentTxnType.COVER_SHORT_SELL:
        case InvestmentTxnType.EXPENSE_MARGIN_INTEREST:
        case InvestmentTxnType.EXPENSE_MISCELLANEOUS:
        case InvestmentTxnType.SELL:
        case InvestmentTxnType.INCOME_DIVIDEND:
        case InvestmentTxnType.INCOME_INTEREST:
        case InvestmentTxnType.CAPITAL_GAIN_LONG:
        case InvestmentTxnType.CAPITAL_GAIN_SHORT:
        case InvestmentTxnType.INCOME_MISCELLANEOUS:
        case InvestmentTxnType.RETURN_OF_CAPITAL:
        case InvestmentTxnType.SELL_BONDS:
        case InvestmentTxnType.SHORT_SELL:
        case InvestmentTxnType.ADD_SHARES:
        case InvestmentTxnType.REINVEST_DIVIDEND:
        case InvestmentTxnType.REINVEST_INTEREST:
        case InvestmentTxnType.REINVEST_CAPITAL_GAIN_LONG:
        case InvestmentTxnType.REINVEST_CAPITAL_GAIN_SHORT:
          pricePerUnit = Math.abs((costBasis - (transaction.commission || 0)) / transaction.units);
          break;
        default:
      }
    }
  }
  return pricePerUnit;
};

export const calcUnits = (transaction: TransactionProps, pricePerUnit: number | string) => {
  let units = 0;
  const pricePerUnitNumber = Number(pricePerUnit);
  const costBasis = Number(transaction?.costBasis || transaction?.amount);
  if (pricePerUnitNumber && !Number.isNaN(pricePerUnitNumber && !Number.isNaN(costBasis)) && costBasis) {
    units = (costBasis - (transaction.commission || 0)) / pricePerUnitNumber;
  }
  return units;
};

export const security = (
  transaction: TransactionProps | null | undefined,
  security: SecurityProps | null | undefined,
) => {
  let { symbol, securityName } = transaction || {};
  if (transaction?.localSecurityId) {
    const securityFound =
      security || (store ? securitiesSelectors.getResourceById(store.getState(), transaction.localSecurityId) : false);
    if (securityFound) {
      symbol = securityFound.symbol;
      securityName = securityFound.name;
    }
  }
  return symbol && securityName ? `${symbol} - ${securityName}` : symbol || securityName || undefined;
};

const invalidInvestmentTxnTypes = [InvestmentTxnType.UNKNOWN, undefined, null, '', 'VIRTUAL_CASH_FLOW'] as const;
export const isValidInvestmentTxnType = (investmentTxnType: any) =>
  !invalidInvestmentTxnTypes.includes(investmentTxnType);

export const isInvestmentUnitsEditable = (investmentTxnType: string | null | undefined) => {
  switch (investmentTxnType) {
    case InvestmentTxnType.EXPENSE_MARGIN_INTEREST:
    case InvestmentTxnType.EXPENSE_MISCELLANEOUS:
    case InvestmentTxnType.CAPITAL_GAIN_LONG:
    case InvestmentTxnType.CAPITAL_GAIN_SHORT:
    case InvestmentTxnType.INCOME_DIVIDEND:
    case InvestmentTxnType.INCOME_INTEREST:
    case InvestmentTxnType.INCOME_MISCELLANEOUS:
    case InvestmentTxnType.RETURN_OF_CAPITAL:
    case InvestmentTxnType.STOCK_SPLIT: // TODO: figure out if we want to use units for adjustment
    case InvestmentTxnType.UNKNOWN:
    case undefined:
    case null:
    case 'VIRTUAL_CASH_FLOW':
      return false;
    default:
      return true;
  }
};

export const isInvestmentPricePerUnitEditable = (investmentTxnType: string | null | undefined) => {
  switch (investmentTxnType) {
    case InvestmentTxnType.EXPENSE_MARGIN_INTEREST:
    case InvestmentTxnType.EXPENSE_MISCELLANEOUS:
    case InvestmentTxnType.CAPITAL_GAIN_LONG:
    case InvestmentTxnType.CAPITAL_GAIN_SHORT:
    case InvestmentTxnType.INCOME_DIVIDEND:
    case InvestmentTxnType.INCOME_INTEREST:
    case InvestmentTxnType.INCOME_MISCELLANEOUS:
    case InvestmentTxnType.REMOVE_SHARES:
    case InvestmentTxnType.RETURN_OF_CAPITAL:
    case InvestmentTxnType.STOCK_DIVIDEND:
    case InvestmentTxnType.STOCK_SPLIT:
    case InvestmentTxnType.UNKNOWN:
    case undefined:
    case null:
    case 'VIRTUAL_CASH_FLOW':
      return false;
    default:
      return true;
  }
};

export const isInvestmentCommissionEditable = (investmentTxnType: string | null | undefined) => {
  switch (investmentTxnType) {
    case InvestmentTxnType.ADD_SHARES:
    case InvestmentTxnType.EXPENSE_MARGIN_INTEREST:
    case InvestmentTxnType.EXPENSE_MISCELLANEOUS:
    case InvestmentTxnType.CAPITAL_GAIN_LONG:
    case InvestmentTxnType.CAPITAL_GAIN_SHORT:
    case InvestmentTxnType.INCOME_DIVIDEND:
    case InvestmentTxnType.INCOME_INTEREST:
    case InvestmentTxnType.INCOME_MISCELLANEOUS:
    case InvestmentTxnType.REMOVE_SHARES:
    case InvestmentTxnType.RETURN_OF_CAPITAL:
    case InvestmentTxnType.STOCK_DIVIDEND:
    case InvestmentTxnType.STOCK_SPLIT:
    case InvestmentTxnType.UNKNOWN:
    case undefined:
    case null:
    case 'VIRTUAL_CASH_FLOW':
      return false;
    default:
      return true;
  }
};

const searchDateFormats = ['M/d/yyyy', 'MMMd,yyyy', 'MMMMd,yyyy', 'M/yyyy', 'MMM,yyyy', 'MMMMyyyy', 'MMMyyyy'] as const;

const cleanString = (str: string) => str.split(' ').join('');

function isValidDate(dateString: string) {
  return searchDateFormats.some((fmt) => DateTime.fromFormat(cleanString(dateString), fmt).isValid);
}

function convertFilterToDateRange(dateString: string) {
  let val1 = DateTime.local();
  let val2 = val1;

  searchDateFormats.some((fmt) => {
    const dt = DateTime.fromFormat(cleanString(dateString), fmt);
    if (dt.isValid) {
      val1 = dt;
      if (fmt.indexOf('d') !== -1) {
        val2 = dt;
      } else {
        val2 = dt.plus({ months: 1 });
      }
    }
    return false;
  });
  return { val1, val2 };
}

export function makeTagsString(
  tags?: ImmutableList<tagsTypes.TagProps> | Array<tagsTypes.TagProps> | null,
  useHashes?: boolean,
) {
  let str: string | null = null;
  if (tags) {
    tags.forEach((x) => {
      const tag = (store ? tagsSelectors.getTagById(store.getState(), getId(x)) : null) || x;
      if (tag && tag.name) {
        str = str ? `${str}${useHashes ? ' ' : '/'}` : '';
        str = `${str}${useHashes ? `#${tag.name}` : tag.name}`;
      }
    });
  }
  return str;
}

export function makeProjectString(projectId?: string | null) {
  return projectId && store ? projectsSelectors.getProjectNameById(store?.getState(), projectId) : null;
}

export function makeCustomerString(customerId?: string | null) {
  if (!customerId) {
    return null;
  }
  const customer = customerId && store ? customersSelectors.getCustomerById(store?.getState(), customerId) : null;
  const address = customer?.address
    ? `${customer.address.city ?? ''}
    ${customer.address.state ?? ''} ${customer.address.zipCode ?? ''} ${customer.address.line1 ?? ''}
    ${customer.address.line2 ?? ''} ${customer.address.country ?? ''}`
    : null;

  return `${customer?.businessName ?? ''} ${customer?.firstName ?? ''} ${customer?.lastName ?? ''} ${address}`?.trim();
}

/**
 * SMART SEARCH tricks
 *
 *
 * If the string typed in evaluates to a number (Not NaN), then we treat it as a number.
 *
 * If the user starts expression with a '-' or a '+' it restricts the search to positive or negative values only
 * If the user types an & sign before the number, we look for an amount in the range of the 2 following comma separated values(Not comma separated anymore, now separated by ".to.")
 * If the user types a >, we look for all income and expenses whose absolute value is greater than the amount given
 * If the user types a <, we look for all income and expenses whose absolute value is less than the amount given
 * If the user types a ~ sign before the expression, or adds 'ish' to the end of it, we look for values relatively close to the value
 *
 * If the filter typed in is a valid DATE, then DATE SEARCH is used
 * >date is all dates after this date
 * <date is all dates before this date
 * ~ at the beginning, or 'ish' added to the end will search 15 days on either side of the date
 * &date,date will search for dates between the 2 given dates
 *
 * For TEXT searches, we remove all punctuation characters (from both the search string, and the string bases search targets
 * UNLESS the user types a '=' as the first character, then an exact string match is considered, including case
 * ALL text searching is case insensitive
 *
 * You may combine expressions using the boolean 'AND' operator expressed as .and.
 *   "+>02/23/2020 .and. Amazon"
 *
 * If the filter expression (can be between .AND. and .OR.) is 'reviewed' or 'notreviewed' then it will filter based on reviewed status
 *
 */
export function filterTransactionsByExpression(
  txns: Array<TransactionProps> | ImmutableList<TransactionProps>,
  _filter: string,
) {
  if (_filter && _filter.length > 0) {
    const expressions = _filter.split('.AND.');

    // if multiple expressions, successively filter them
    if (expressions.length > 1) {
      let results = txns;
      expressions.forEach((phrase) => {
        results = filterTransactionsByExpression(results, phrase.trim());
      });
      return results;
    }

    let filter = _filter;
    let fuzzy = false;

    // Is this a fuzzy search?
    if (filter.slice(-3) === 'ish') {
      filter = filter.slice(0, -3);
      fuzzy = true;
    }
    // Does user want a fuzzy search
    if (filter[0] === '~') {
      fuzzy = true;
      filter = filter.slice(1);
    }

    let numberSearch = false;
    let dateSearch = false;
    let val1: DateTime | number | null = null;
    let val2: DateTime | number | null = null;

    // Does user want to restrict values to only income or expense?
    const negOnly = filter[0] === '-';
    const posOnly = filter[0] === '+';
    const startChar = negOnly || posOnly ? 1 : 0;
    let prefix = filter[startChar];

    // Does user want a less than/greater than/ or range search?
    if (prefix === '>' || prefix === '<' || prefix === '&') {
      const values = filter.slice(startChar + 1).split('.to.');
      if (!Number.isNaN(Number(values[0]))) {
        val1 = Number(values[0]);
        val2 = values.length > 1 ? Number(values[1]) : 0;
        numberSearch = !Number.isNaN(val1) && !Number.isNaN(val2);
      } else if (isValidDate(values[0])) {
        // date?
        val1 = convertFilterToDateRange(values[0]).val1;
        val2 = values.length > 1 ? convertFilterToDateRange(values[1]).val2 : DateTime.local();
        dateSearch = val1.isValid && val2.isValid;
      }
      // No PREFIX, so if text is a date or a number, apply a special search to them
    } else if (!Number.isNaN(Number(filter))) {
      numberSearch = true;
      if (fuzzy) {
        const filterAsAmount = Number(filter);
        const variancePct = 0.02;
        // construct a range search
        val2 = Number((Number(filterAsAmount) * (1 + variancePct)).toFixed(2));
        val1 = Number((Number(filterAsAmount) * (1 - variancePct)).toFixed(2));
        prefix = '&';
      }
    } else if (isValidDate(filter)) {
      dateSearch = true;
      const dateSpread = fuzzy ? 5 : 0;
      const dateRange = convertFilterToDateRange(filter);
      val1 = dateRange.val1.minus({ days: dateSpread });
      val2 = dateRange.val2.plus({ days: dateSpread });
      prefix = '&';
    }

    // First, remove apostophes from the search
    let filterClean = filter.replace("'", '');
    filterClean = filterClean.replace('`', '');
    // Convert special characters to spaces for the search
    filterClean = filterClean.replace(/([!"#%&'*()+,\-./:;<=>?\][@\\^_])/g, ' ');

    const baseValue = Number(filter.slice(negOnly || posOnly ? 1 : 0)); // with math operand removed
    const exactDecimals = filter.includes('.');

    const filterCallback = (txn: TransactionProps) => {
      /*
       * AMOUNT SEARCH
       */
      if (numberSearch) {
        if (
          txn.type === 'INVESTMENT' &&
          (baseValue === (exactDecimals ? Math.abs(txn.units ?? 0) : Math.floor(Math.abs(txn.units ?? 0))) ||
            baseValue === (exactDecimals ? Math.abs(txn.costBasis ?? 0) : Math.floor(Math.abs(txn.costBasis ?? 0))) ||
            baseValue === (exactDecimals ? Math.abs(txn.commission ?? 0) : Math.floor(Math.abs(txn.commission ?? 0))))
        ) {
          return true;
        }

        if ((negOnly && (txn.amount ?? 0) >= 0) || (posOnly && (txn.amount ?? 0) <= 0)) {
          return false;
        }
        const txnAmt = Math.abs(Number(txn.amount));
        switch (prefix) {
          case '>':
            return Math.abs(txnAmt) >= (val1 as number);
          case '<':
            return Math.abs(txnAmt) <= (val1 as number);
          case '&': {
            val1 = Math.abs(val1 as number);
            val2 = Math.abs(val2 as number);
            if (val1 > val2) {
              return txnAmt >= val2 && txnAmt <= val1;
            }
            return txnAmt >= val1 && txnAmt <= val2;
          }

          default:
            if (baseValue === (exactDecimals ? txnAmt : Math.floor(txnAmt))) {
              return true;
            }
        }
      }

      /*
       * DATE SEARCH
       */
      if (dateSearch) {
        const txnDate = DateTime.fromISO(txn.postedOn);
        switch (prefix) {
          case '>':
            return txnDate >= (val1 ?? 0);
          case '<':
            return txnDate <= (val1 ?? 0);
          case '&':
            return txnDate >= (val1 ?? 0) && txnDate <= (val2 ?? 0);
          default:
            return false;
        }
      }

      /*
       * NON-AMOUNT SEARCH
       */
      if (filter === 'reviewed' || filter === 'notreviewed') {
        return txn.isReviewed ? filter === 'reviewed' : !(filter === 'reviewed');
      }
      let descString = `${txn.payee}~`;
      descString = `${descString}${DateTime.fromISO(txn.postedOn).toFormat('MMM dd,yyyy')}~`;
      if (txn.coa) {
        // TODO leverage longCats txlist preference to match search to display of category
        descString = `${descString}${chartOfAccountsSelectors.getCoaStringSelector(undefined, txn.coa)}~`;
      }
      descString = `${descString}${txn.state}~`;
      if (txn.check && txn.check.number) {
        descString = `${descString}${txn.check.number}~`;
      }
      // investments meta data
      if (txn.type === 'INVESTMENT') {
        const humanReadableType = txn.investmentTxnType ? InvestmentTxnActionsByType[txn.investmentTxnType] : '';
        descString = `${descString}${humanReadableType}~`;
        descString = `${descString}${txn.symbol}~`;
        descString = `${descString}${txn.securityName}~`;
      }
      descString = `${descString}${String(numeral(txn.amount).format('$0,00.00'))}~`;

      if (txn.split && txn.split.items) {
        txn.split.items.forEach((item) => {
          // TODO leverage longCats txlist preference to match search to display of category
          descString = `${descString}${item.memo}~${String(item.amount)}~${makeTagsString(
            item.tags,
          )}~${chartOfAccountsSelectors.getCoaStringSelector(undefined, item.coa)}~
          ${makeProjectString(item.projectId)}~
          ${makeCustomerString(item.customerId)}~`;
        });
      }
      if (txn.projectId) {
        descString = `${descString}${makeProjectString(txn.projectId)}~`;
      }
      if (txn.customerId) {
        descString = `${descString}${makeCustomerString(txn.customerId)}~`;
      }
      if (txn.tags) {
        descString = `${descString}${makeTagsString(txn.tags)}~`;
      }
      if (txn.memo) {
        descString = `${descString}${txn.memo}~`;
      }

      // search by id for internal purpose
      descString = `${descString}${txn.id}~`;

      // First, remove apostophes from the search
      descString = descString.replace("'", '');
      descString = descString.replace('`', '');
      // Convert special characters to spaces for the search
      descString = descString.replace(/([!"#%&*'()+,`\-./:;<=>?\][@\\^_])/g, ' ');

      return descString.toLowerCase().indexOf(filterClean.toLowerCase()) >= 0;
    };
    // The type signatures of array.filter and list.filter are technically not compatible,
    // but we're only using the first arg of the callback so it doesn't make a difference.
    // this is pointless check is to appease TS.
    return 'size' in txns ? txns.filter(filterCallback) : txns.filter(filterCallback);
  }
  return txns;
}

export function txnHasCoa(txn: TransactionProps, coaId: string) {
  if (!isSplitTxn(txn)) {
    return (
      txn.coa?.id === coaId ||
      (txn.coa?.type === 'UNCATEGORIZED' && coaId === 'UNCATEGORIZED') ||
      (txn.coa?.type === 'BALANCE_ADJUSTMENT' && coaId === `BALANCE_ADJUSTMENT_${txn.coa?.id}`)
    );
  }
  return txn.split?.items?.find(
    (item) => item.coa?.id === coaId || (item.coa?.type === 'UNCATEGORIZED' && coaId === 'UNCATEGORIZED'),
  );
}

export function txnHasTag(txn: TransactionProps, tagId: string) {
  if (!isSplitTxn(txn)) return txn.tags?.find((x) => x.id === tagId);
  return txn.split?.items?.find((item) => Boolean(item.tags?.find((x) => x.id === tagId)));
}

/*
 Filter Transactions By Filter Object

 Note, the first parameter is an immutable list of transactions

 Filter Object at this time is a dot addressable object (JS object or Immutable Record) with the following optional keys
 coas,                    List of coa objects that include type and id
 tagsIds,                 List of tag ID's that should be included
 payeeNames,                  list of payee names to be included
 flags,                   list of qcs colors or NONE,
 accounts,                list of accountIds to be included (if not specified, it is all accounts)
 businesses,              list of business Ids to be included
 isBillOrSubscription,    if set (not undefined or null), it will filter by whether txns are bills or not
 isExcludedFromReports,    if set (not undefined or null), it will filter by whether txns have their ignored flag set
 dateStart                iso date string or luxon date
 dateEnd                  iso date string or luxon date
 */
export const createTransactionsFilterByObjFn = (filterObject: filtersTypes.FilterObject | null | undefined) => {
  const dateStartISO =
    typeof filterObject?.dateStart === 'string' ? filterObject?.dateStart : filterObject?.dateStart?.toISODate();
  const dateEndISO =
    typeof filterObject?.dateEnd === 'string' ? filterObject?.dateEnd : filterObject?.dateEnd?.toISODate();
  return (txn: TransactionProps) => {
    let keep = true;

    if (keep && filterObject?.advanced?.isBillOrSubscription != null) {
      keep = isBillOrSubscription(txn) === filterObject.advanced?.isBillOrSubscription;
    }
    if (keep && filterObject?.advanced?.isExcludedFromReports != null) {
      keep = Boolean(txn.isExcludedFromReports) === filterObject.advanced?.isExcludedFromReports;
    }
    if (keep && filterObject?.advanced?.isReviewed != null) {
      keep = Boolean(txn.isReviewed) === filterObject.advanced?.isReviewed;
    }
    if (keep && filterObject?.advanced?.isExcludedFromF2S != null) {
      keep = Boolean(txn.isExcludedFromF2S) === filterObject.advanced?.isExcludedFromF2S;
    }

    if (keep && filterObject?.accounts?.size) keep = filterObject?.accounts.includes(txn.accountId ?? '');
    if (keep && filterObject?.payeeNames?.size)
      keep = filterObject?.payeeNames?.map((payee) => payee.toLowerCase()).includes(txn.payee?.toLowerCase() ?? '');
    if (keep && filterObject?.businesses?.size) {
      if (txn.businessId === '0' || txn.businessId === undefined) {
        keep = filterObject.businesses.has('PERSONAL');
      } else {
        keep = txn.businessId ? filterObject.businesses.has(txn.businessId) : false;
      }
    }
    if (keep && filterObject?.flags?.size) {
      if (filterObject?.flags.has('NONE')) {
        keep = txn.userFlag === null;
      } else {
        keep = filterObject?.flags.includes(txn.userFlag ?? '');
      }
    }
    if (keep && filterObject?.categories?.size)
      keep = Boolean(filterObject?.categories.find((coaId) => Boolean(txnHasCoa(txn, coaId))));
    if (keep && filterObject?.tags?.size)
      keep = Boolean(filterObject?.tags.find((tagId) => Boolean(txnHasTag(txn, tagId))));

    if (keep && dateStartISO && txn.postedOn) keep = txn.postedOn >= dateStartISO;
    if (keep && dateEndISO && txn.postedOn) keep = txn.postedOn <= dateEndISO;

    return filterObject?.filterOut ? !keep : keep;
  };
};

export const filterTransactionsByFilterObject = (
  txns: Array<TransactionProps> | Collection<string | number, TransactionProps>,
  filterObject: filtersTypes.FilterObject | null | undefined,
) =>
  Array.isArray(txns)
    ? txns.filter(createTransactionsFilterByObjFn(filterObject))
    : txns.filter(createTransactionsFilterByObjFn(filterObject));

export const SortModes = Object.freeze({
  PENDING_TO_CLEARED: 'PENDING_TO_CLEARED',
  DATE_OLD_TO_NEW: 'DATE_OLD_TO_NEW',
  DATE_NEW_TO_OLD: 'DATE_NEW_TO_OLD',
  AMOUNT_HIGH_TO_LOW: 'AMOUNT_HIGH_TO_LOW',
  AMOUNT_LOW_TO_HIGH: 'AMOUNT_LOW_TO_HIGH',
});

const collator = new Intl.Collator();
const numericCollator = new Intl.Collator(undefined, { sensitivity: 'case', numeric: true });
const subTransactionsCompare = (transaction1: TransactionProps, transaction2: TransactionProps) =>
  collator.compare(transaction2.postedOn, transaction1.postedOn) ||
  numericCollator.compare(transaction2.payee ?? '', transaction1.payee ?? '');
export const createTransactionsCompareByModeFn =
  (sortMode: (typeof SortModes)[keyof typeof SortModes] = SortModes.PENDING_TO_CLEARED) =>
  (transaction1: TransactionProps, transaction2: TransactionProps) => {
    switch (sortMode) {
      case SortModes.PENDING_TO_CLEARED:
        return (
          (transaction1.state === 'PENDING' ? 0 : 1) - (transaction2.state === 'PENDING' ? 0 : 1) ||
          subTransactionsCompare(transaction1, transaction2)
        );
      case SortModes.DATE_OLD_TO_NEW:
        return collator.compare(transaction1.postedOn, transaction2.postedOn);
      case SortModes.DATE_NEW_TO_OLD:
        return collator.compare(transaction2.postedOn, transaction1.postedOn);
      case SortModes.AMOUNT_HIGH_TO_LOW:
        return (
          (transaction1.amount ?? 0) - (transaction2.amount ?? 0) || subTransactionsCompare(transaction1, transaction2)
        );
      case SortModes.AMOUNT_LOW_TO_HIGH:
        return (
          (transaction2.amount ?? 0) - (transaction1.amount ?? 0) || subTransactionsCompare(transaction1, transaction2)
        );
      default:
        assert(false, `unexpected sortMode = ${sortMode}`);
        return 0;
    }
  };

export const isBillOrSubscription = (txn: TransactionProps | null | undefined) =>
  txn?.isBill || txn?.isSubscription || false;

// # ------------------------------------------------------------------------- #
// #                             Pre-Process Utils                             #
// # ------------------------------------------------------------------------- #

// -------------------------------------------------------
export function setReportsFlags(txn: TransactionRecord, value: any) {
  return txn && txn.set('isExcludedFromReports', Boolean(value)).set('isExcludedFromF2S', Boolean(value));
}

function currenciesAreValidAndDifferent(a: any, b: any) {
  if (a === 'N/A' || b === 'N/A') {
    return false;
  }
  return a !== b;
}
// -------------------------------------------------------
export function isMultiCurrencyTransferTxn(txn: TransactionProps, state?: RootState | null) {
  let isMultiCurrency = false;
  const txnCurrency = accountsUtils.getCurrencyString(txn.accountId, state);

  if (isTransferTxn(txn)) {
    if (isSplitTxn(txn)) {
      txn.split?.items?.forEach((item) => {
        if (item.coa?.type === 'ACCOUNT') {
          isMultiCurrency =
            isMultiCurrency ||
            currenciesAreValidAndDifferent(accountsUtils.getCurrencyString(item.coa.id, state), txnCurrency);
        }
      });
    } else {
      isMultiCurrency = currenciesAreValidAndDifferent(
        accountsUtils.getCurrencyString(txn.coa?.id, state),
        txnCurrency,
      );
    }
  }
  return isMultiCurrency;
}

// -------------------------------------------------------
export function isUnsyncedTransferTxn(txn: TransactionProps, state?: RootState) {
  if (isSplitTxn(txn)) {
    return Boolean(txn.split?.items?.find((item) => chartOfAccountsUtils.isUnsyncedTransferCoa(item.coa, state)));
  }
  return chartOfAccountsUtils.isUnsyncedTransferCoa(txn.coa, state);
}

// -------------------------------------------------------
// TODO: change the feature flags type when the flux/feature-flags module is converted to TS
export function txnHasUnsupportedTransfer(
  txn: Partial<TransactionProps>,
  featureFlags: ImmutableMap<string, boolean>,
  state: Pick<RootState, accountsTypes.ACCOUNTS_REDUCER_KEY | typeof FEATURE_FLAGS_REDUCER_KEY>,
) {
  if (!isTransferTxn(txn)) return false;

  const hideLoanTransactions = featureFlags.get('hideLoanTransactions');
  const hideConnectedLoanTransactions = featureFlags.get('hideConnectedLoanTransactions');

  if (isSplitTxn(txn)) {
    return Boolean(
      txn.split?.items?.find(
        (item) =>
          item.coa?.type === 'ACCOUNT' &&
          accountsUtils.transactionsNotSupportedForAccountId(
            item.coa.id,
            hideLoanTransactions,
            hideConnectedLoanTransactions,
            state,
          ),
      ),
    );
  }
  return accountsUtils.transactionsNotSupportedForAccountId(
    txn.coa?.id,
    hideLoanTransactions,
    hideConnectedLoanTransactions,
    state,
  );
}
// -------------------------------------------------------
export function isSupportedTransferTxnWithFlags(
  txn: TransactionProps,
  featureFlags: ImmutableMap<string, boolean>,
  state: Pick<RootState, ACCOUNTS_REDUCER_KEY | typeof FEATURE_FLAGS_REDUCER_KEY>,
) {
  if (!isTransferTxn(txn)) return false;
  return !txnHasUnsupportedTransfer(txn, featureFlags, state);
}

// -------------------------------------------------------
export function isLoanAcctTransferTxn(txn: TransactionProps, state: RootState) {
  if (isSplitTxn(txn)) {
    return Boolean(txn.split?.items?.find((item) => chartOfAccountsUtils.isLoanAcctTransferCoa(item.coa, state)));
  }
  return chartOfAccountsUtils.isLoanAcctTransferCoa(txn.coa, state);
}

// -------------------------------------------------------
export function isInvestmentAcctTransferTxn(txn: TransactionProps, state: RootState) {
  if (isSplitTxn(txn)) {
    return Boolean(txn.split?.items?.find((item) => chartOfAccountsUtils.isInvestmentAcctTransferCoa(item.coa, state)));
  }
  return chartOfAccountsUtils.isInvestmentAcctTransferCoa(txn.coa, state);
}

export function isPendingTxn(txn: TransactionProps | null) {
  return txn && txn.state === 'PENDING';
}

// When entering a new transfer transaction, looks for an already existing transaction that may
// be a possible match for user prompting
export function addPossibleMatch(
  match: TransactionRecord,
  baseTxn: TransactionRecord,
  xfrTxn: TransactionRecord,
  allMatches: Array<{
    matchingNewTxn: TransactionRecord;
    possibleMatch: TransactionRecord;
    baseTxn: TransactionRecord;
    forceWarn: boolean;
  }>,
  forceWarn = false,
) {
  const possibleMatch = match.merge({
    coa: { id: baseTxn.accountId ?? '', type: 'ACCOUNT' },
    isExcludedFromF2S: true,
    isExcludedFromReports: true,
    transfer: { id: baseTxn?.id, clientId: baseTxn?.clientId },
  });
  const updatedBase = baseTxn.set('transfer', { clientId: possibleMatch?.clientId, id: possibleMatch?.id });
  allMatches.push({ matchingNewTxn: xfrTxn, possibleMatch, baseTxn: updatedBase, forceWarn });
}

//-------------------------------------------------------
export function findTransaction(
  txnsByAccountId: ImmutableMap<string, ImmutableMap<string, TransactionRecord>>,
  txnId: string,
  accountId?: string,
) {
  if (accountId) {
    const acctTxns = txnsByAccountId?.get(accountId);
    if (acctTxns) {
      const foundTxn = acctTxns.get(txnId);
      if (foundTxn) return foundTxn;
    }
  }

  // slow way
  let ret: TransactionRecord | null = null;
  txnsByAccountId.some((txnList) => {
    const foundTxn = txnList.get(txnId);
    if (foundTxn) {
      ret = foundTxn;
      return true;
    }
    return false;
  });
  return ret;
}

//-------------------------------------------------------
export function sortTxnsByDateDifference(txns: ImmutableList<TransactionProps>, referenceDate: string) {
  const referenceDateTime = DateTime.fromISO(referenceDate);
  return txns.sort((a, b) => {
    const diff1 = Math.abs(DateTime.fromISO(a.postedOn).diff(referenceDateTime, 'days').days);
    const diff2 = Math.abs(DateTime.fromISO(b.postedOn).diff(referenceDateTime, 'days').days);
    if (diff1 === diff2) {
      return 0;
    }
    if (diff1 > diff2) {
      return 1;
    }
    return -1;
  });
}

// The code below is used to find possible transfers for new transactions being inserted.  When a new
// transfer transaction is entered, we look in the transfer account for a possible transaction match that
// may already be there, and offer the user to match to that transaction rather than create a dup
//
function isPossibleMatch(txn: TransactionProps, postedOn: string, amount: number, days = 31) {
  const diff = DateTime.fromISO(postedOn).diff(DateTime.fromISO(txn.postedOn), 'days').days;
  return !isSplitTxn(txn) && !isTransferTxn(txn) && Math.abs(diff) <= days && Number(amount) === Number(txn.amount);
}
//-------------------------------------------------------
export function findPossibleTransferCandidate(
  accountId: string,
  postedOn: string,
  amount: number,
  txnsByAccountId: ImmutableMap<string, ImmutableMap<string, TransactionRecord>>,
  days?: number,
) {
  const acctTxns = txnsByAccountId.get(accountId) || ImmutableMap();
  const txns = acctTxns.filter((tx) => isPossibleMatch(tx, postedOn, amount, days)).toList();
  if (txns) {
    return sortTxnsByDateDifference(txns, postedOn).first();
  }
  return null;
}
//-------------------------------------------------------
export function possiblyMatchTransfer(
  _txn: TransactionRecord,
  txnsByAccountId: ImmutableMap<string, ImmutableMap<string, TransactionRecord>>,
) {
  let txn = _txn;
  if (!isSplitTxn(txn) && !isTransferTxn(txn) && chartOfAccountsUtils.isL1TransferCoa(txn.coa)) {
    let matches = ImmutableList<TransactionProps>();
    txnsByAccountId.forEach((_acctTxns, acctId) => {
      if (acctId !== txn.accountId) {
        // check if there is a matching transaction and score it
        const match = findPossibleTransferCandidate(acctId, txn.postedOn, -(txn.amount ?? 0), txnsByAccountId, 5);
        if (match) {
          matches = matches.push(match);
        }
      }
    });
    if (matches.size > 0) {
      const bestMatch = sortTxnsByDateDifference(matches, txn.postedOn).first();
      txn = txn.set('coa', { type: 'ACCOUNT', id: bestMatch?.accountId ?? '' });
    }
  }
  return txn;
}

//-------------------------------------------------------
export function coasAreDifferent(
  coa1: chartOfAccountsTypes.ChartOfAccountProps | null | undefined,
  coa2: chartOfAccountsTypes.ChartOfAccountProps | null | undefined,
) {
  if (!coa1 && !coa2) return false;
  if (!coa1 || !coa2) return true;
  return coa1.type !== coa2.type || coa1.id !== coa2.id;
}

//-------------------------------------------------------
export function areTagsEqual(
  tags1: Array<tagsTypes.TagProps> | ImmutableList<tagsTypes.TagProps> | null | undefined,
  tags2: Array<tagsTypes.TagProps> | ImmutableList<tagsTypes.TagProps> | null | undefined,
) {
  if (
    (!tags1 || ('length' in tags1 && tags1.length === 0) || ('size' in tags1 && tags1.size === 0)) &&
    (!tags2 || ('length' in tags2 && tags2.length === 0) || ('size' in tags2 && tags2.size === 0))
  ) {
    // both either null or empty
    return true;
  }
  if (!tags1 || !tags2) return false;
  const sortCallback = (a: tagsTypes.TagProps, b: tagsTypes.TagProps) => (getId(a) > getId(b) ? 1 : -1);
  const reduceCallback = (ids: string, tag: tagsTypes.TagProps) => `${ids}-${getId(tag)}`;

  // technically the reduce methods on arrays and immutable lists are not compatible, but we are only using the common arguments so we do this type dance to make the compiler happy
  const sum1 = Array.isArray(tags1)
    ? tags1.sort(sortCallback).reduce(reduceCallback, '')
    : tags1.sort(sortCallback).reduce(reduceCallback, '');

  const sum2 = Array.isArray(tags2)
    ? tags2.sort(sortCallback).reduce(reduceCallback, '')
    : tags2.sort(sortCallback).reduce(reduceCallback, '');

  return sum1 === sum2;
}

function splitStringVal(item: SplitItemProps) {
  return `${item.coa?.id}-${Number(item.amount).toFixed(2)}-${item.memo}-${
    item.tags ? (Array.isArray(item.tags) ? item.tags.length : item.tags.size) : 'none'
  }`;
}
//-------------------------------------------------------
export function fieldChanged(txn: TransactionProps, txnPrev: TransactionProps, field: keyof TransactionProps) {
  // this is checking if one or the other is null, however, we have to do a bit more
  // for splits since even though .split may not be null, it can still be a non-split if
  // there are no items

  // should not be called with either value being null
  if (!txn || !txnPrev) return false;

  if (!txn[field] || !txnPrev[field]) {
    return field === 'split'
      ? isSplitTxn(txn) !== isSplitTxn(txnPrev)
      : Boolean(txn[field]) !== Boolean(txnPrev[field]);
  }
  switch (field) {
    case 'amount': {
      return Number(txnPrev.amount) !== Number(txn.amount);
    }

    case 'postedOn': {
      return txnPrev.postedOn !== txn.postedOn;
    }

    case 'coa': {
      return coasAreDifferent(txnPrev.coa, txn.coa); // ((txnPrev.coa.id !== txn.coa.id) || (txnPrev.coa.type !== txn.coa.type));
    }

    case 'check': {
      return txnPrev.check?.number !== txn.check?.number;
    }

    case 'attachments': {
      const sum1 = txn[field]?.sort((a, b) => (a > b ? 1 : -1)).reduce((total, num) => `${total}-${num}`, '');
      const sum2 = txnPrev[field]?.sort((a, b) => (a > b ? 1 : -1)).reduce((total, num) => `${total}-${num}`, '');
      return sum1 !== sum2;
    }

    case 'tags': {
      return !areTagsEqual(txn[field], txnPrev[field]);
    }

    case 'split': {
      if (isSplitTxn(txn) !== isSplitTxn(txnPrev)) {
        return true;
      }
      const sortCb = (a: SplitItemProps, b: SplitItemProps) => ((a.coa?.id ?? '') > (b.coa?.id ?? '') ? 1 : -1);
      const reduceCb = (total: string, item: SplitItemProps) => `${total}-${splitStringVal(item)}`;

      const sorted1 = txnPrev.split?.items?.sort(sortCb);
      let sum1: string | undefined;
      // Technically the reduce function on an immutable list has more args than the
      // reduce function on an array, so we have to explicitly do this if/else to make TS happy.
      // Our callback is only using the first few common args though so it seems a little pointless.
      if (sorted1 && Array.isArray(sorted1)) {
        sum1 = sorted1.reduce(reduceCb, '');
      } else if (sorted1) {
        sum1 = sorted1.reduce(reduceCb, '');
      }

      const sorted2 = txn.split?.items?.sort(sortCb);
      let sum2: string | undefined;
      if (sorted2 && Array.isArray(sorted2)) {
        sum2 = sorted2.reduce(reduceCb, '');
      } else if (sorted2) {
        sum2 = sorted2.reduce(reduceCb, '');
      }

      return sum1 !== sum2;
    }
    default:
      if (txnPrev) {
        return txn[field] !== txnPrev[field];
      }
  }
  return false;
}

const noNaN = (n: string) => (Number.parseFloat(n) ? n : 0);
// -------------------------------------------------------
// takes an immutable transaction, and checks for tags with ID="0" and tries to
// find a tag that is suitable with a real ID.  Returns an immutable transaction
// with updates if they were needed
export function updateNewTags(txn: TransactionRecord, dropPlaceholders = false, state = store?.getState()) {
  let retTxn = txn;
  if (txn.tags) {
    const visited: tagsTypes.TagProps[] = [];
    const filterTag = (tag: tagsTypes.TagProps) => {
      // remove dupes or empty entries
      if (tag) {
        const alreadyHas = visited.find((x) => x.id === tag.id && (x.id !== '0' || x.name === tag.name));
        if (!alreadyHas) {
          visited.push(tag);
          return true;
        }
      }
      return false;
    };
    const mapTag = (tag: tagsTypes.TagProps) => {
      // find current tag
      if (noNaN(tag.id ?? '') === 0) {
        const ret = state ? tagsSelectors.getTagByName(state, tag.name ?? '') : null;
        return ret && ret.id ? ret : (tag as tagsTypes.Tag);
      }
      return tag as tagsTypes.Tag;
    };
    // Technicaly arrays and immutable lists have different signature for their filter and map methods
    // We are only using the first arg which is the same, but we still have to typecheck to make the compiler happy
    const newTags = Array.isArray(txn.tags)
      ? txn.tags.filter(filterTag).map(mapTag)
      : txn.tags.filter(filterTag).map(mapTag);

    const filterEmpty = (x: tagsTypes.TagProps) => x !== null && (!dropPlaceholders || noNaN(x?.id ?? '') !== 0);
    retTxn = txn.set(
      'tags',
      Array.isArray(newTags)
        ? ImmutableList(newTags.filter(filterEmpty).map((t) => tagsTypes.mkTag(t)))
        : newTags.filter(filterEmpty),
    );
  }
  if (isSplitTxn(retTxn)) {
    const newItems = retTxn.split?.items?.map((item) => {
      if (item?.tags) {
        const newSplitTags = item.tags.map((tag) => {
          if (noNaN(tag.id ?? '') === 0) {
            const ret = store ? tagsSelectors.getTagByName(store.getState(), tag.name ?? '') : null;
            if (ret && ret.id) {
              return ret;
            }
            return dropPlaceholders ? null : tag;
          }
          return tag;
        });
        const filterCb = (x: tagsTypes.TagProps | null) => x !== null && (!dropPlaceholders || noNaN(x.id ?? '') !== 0);
        const filteredTags = Array.isArray(newSplitTags)
          ? (newSplitTags.filter(filterCb) as tagsTypes.Tag[])
          : (newSplitTags.filter(filterCb) as ImmutableList<tagsTypes.Tag>);
        if ('set' in item) {
          // immutable record
          return item?.set('tags', filteredTags) || item;
        }
        const newItem = item;
        newItem.tags = filteredTags;
        return newItem;
      }
      return item;
    });

    retTxn = retTxn.setIn(['split', 'items'], newItems);
  }
  return retTxn;
}

export function isExistingTxn(txn: TransactionProps | null | undefined) {
  return txn?.id && txn.id !== '0';
}

// -------------------------------------------------------
export function isUncategorizedTxn(txn: TransactionProps) {
  return !isSplitTxn(txn) && (!txn.coa || txn.coa.type === 'UNCATEGORIZED');
}

// simple protected dateTime check
export function dateTimeFromTxn(txn: TransactionProps) {
  return (
    (txn.postedOn && DateTime.fromISO(txn.postedOn)) ||
    (txn.stDueOn && DateTime.fromISO(txn.stDueOn)) ||
    DateTime.local()
  ).startOf('day');
}

const protectedLookup = (split: SplitProps | null | undefined, i: number) => {
  if (!split || !split.items) {
    return null;
  }
  return 'get' in split.items ? split?.items.get(i) : split?.items?.[i];
};

// expects txn, optional 'noParent' bool
export function getCategoryDisplayString(txn: TransactionProps, noParent = true) {
  const isSplit = isSplitTxn(txn);
  if (isSplit) {
    const { split } = txn;
    const firstItem = protectedLookup(split, 0);
    const secondItem = protectedLookup(split, 1);
    const thirdItem = protectedLookup(split, 2);
    return `${chartOfAccountsSelectors.getCoaStringSelector(
      null,
      firstItem?.coa,
      true,
    )}, ${chartOfAccountsSelectors.getCoaStringSelector(null, secondItem?.coa, true)}${thirdItem ? ', ...' : ''}`;
  }
  return chartOfAccountsSelectors.getCoaStringSelector(null, txn.coa, noParent);
}

const MISC: string[] = [InvestmentTxnType.EXPENSE_MISCELLANEOUS, InvestmentTxnType.INCOME_MISCELLANEOUS];
export const isExposedTransaction = (
  transaction: TransactionProps,
  accounts: ImmutableMap<string, accountsTypes.Account>,
  exposeInvestmentTransactions: string,
) =>
  accounts.get(transaction.accountId ?? '')?.type !== 'INVESTMENT' ||
  exposeInvestmentTransactions === 'ALL' ||
  (exposeInvestmentTransactions === 'CASH_FLOW+MISC' &&
    (transaction.type === 'CASH_FLOW' || MISC.includes(transaction.investmentTxnType ?? ''))) ||
  (exposeInvestmentTransactions === 'CASH_FLOW' && transaction.type === 'CASH_FLOW') ||
  (exposeInvestmentTransactions === 'HIDE' && false);

export const isConnectedLoanTransaction = (
  txn: TransactionProps,
  accounts: ImmutableMap<string, accountsTypes.Account>,
) => {
  const account = accounts.get(txn.accountId ?? '');
  if (account?.type === 'LOAN' && !accountsUtils.isAccountManual(account)) {
    return true;
  }
  return false;
};

export function getFieldString(field: string, txn: TransactionProps, longCats?: boolean) {
  switch (field) {
    case 'postedOn':
    case 'date':
      return DateTime.fromISO(txn.postedOn).toLocaleString(DateTime.DATE_SHORT);
    case 'category':
      // currently displays 'split' for splits, if you want listed split items than you can use 'getCategoryDisplayString' above
      return isSplitTxn(txn) ? 'SPLIT' : chartOfAccountsSelectors.getCoaStringSelector(undefined, txn.coa, longCats);
    case 'account':
    case 'accountColor':
      return accountsUtils.getAccountString(txn.accountId);
    case 'amount':
      return numberUtils.formatNumber(txn.amount, accountsUtils.getCurrencyString(txn.accountId), '0,00.00');
    case 'balance':
      return numberUtils.formatNumber(txn.balance, accountsUtils.getCurrencyString(txn.accountId), '0,00.00');
    case 'tags': {
      return makeTagsString(txn.tags) || '';
    }
    case 'check': {
      return txn.check && txn.check.number;
    }
    case 'status':
      return txn.state;
    case 'notes':
      return txn.memo ? txn.memo : '';
    default:
      return `${field}` in txn ? (txn as any)[field] : '';
  }
}

export const roundOff = (num: number, places: number) => {
  const x = Math.pow(10, places);
  return Math.round(num * x) / x;
};

export const getTxnSum = (txns: Collection<any, TransactionProps>) =>
  roundOff(txns?.reduce((acc, txn) => acc + (txn.amount || 0), 0) || 0, 2);

export const splitsWithPropertyValue = (split: SplitProps | null | undefined, prop: keyof SplitItemProps) => {
  if (!split || !split.items) {
    return ImmutableList<SplitItemProps>();
  }
  const propFilter = (item: SplitItemProps) => Boolean(item[prop]);
  return Array.isArray(split.items) ? split.items.filter(propFilter) : split.items.filter(propFilter);
};

export const isNonBilledBillableItem = (txnItem: TransactionItem) =>
  Boolean(txnItem.item.isBillable && !txnItem.item.linkedItem?.id);

export const isBilledItem = (txnItem: TransactionItem) =>
  Boolean(txnItem.item.isBillable && txnItem.item.linkedItem?.id);

export const isNonBilledItem = (txnItem: TransactionItem) => Boolean(!txnItem.item.linkedItem?.id);

// the reduce from allocations can be either be an array or a list
export const sumAllocations = (invoice: TransactionProps) => {
  let paid = 0;
  if (!invoice.allocations) {
    return paid;
  }
  if (Array.isArray(invoice.allocations)) {
    paid = invoice.allocations.reduce<number>(
      (s: number, allocation: AllocationProps) => s + (allocation.amount ?? 0),
      0,
    );
  } else if (ImmutableList.isList(invoice.allocations)) {
    paid = invoice.allocations.reduce<number>((s: number, allocation: Allocation) => s + (allocation.amount ?? 0), 0);
  }
  return paid;
};

export const isCheckNumberVisibleOnInvestmentTxnType = (investmentTxnType: string | null | undefined) => {
  switch (investmentTxnType) {
    case 'VIRTUAL_CASH_FLOW':
      return true;
  }

  return false;
};

export const isCategoryVisibleOnInvestmentTxnType = (investmentTxnType: string | null | undefined) => {
  switch (investmentTxnType) {
    case InvestmentTxnType.EXPENSE_MISCELLANEOUS:
    case InvestmentTxnType.INCOME_MISCELLANEOUS:
    case 'VIRTUAL_CASH_FLOW':
      return true;
  }

  return false;
};
